import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Heart, ShoppingCart, Truck, ShieldCheck, RotateCcw, MapPin, Minus, Plus, Star, ChevronRight } from "lucide-react";
import Gallery from "@/components/product/Gallery";
import Rating from "@/components/common/Rating";
import ProductRail from "@/components/product/ProductRail";
import EmptyState from "@/components/common/EmptyState";
import { getProduct, getProducts } from "@/services/catalog";
import { formatPrice, discountPercent } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n/I18nProvider";

export default function ProductPage() {
  const { slug } = useParams();
  const { t } = useI18n();
  const { addToCart, toggleWishlist, inWishlist, pushRecent } = useApp();
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState({});
  const [tab, setTab] = useState("description");

  const { data: product, isLoading } = useQuery({ queryKey: ["product", slug], queryFn: () => getProduct(slug) });

  useEffect(() => { if (product) pushRecent(product); }, [product, pushRecent]);
  useEffect(() => { setQty(1); setVariant({}); }, [slug]);

  const catSlug = product?.categories?.[0]?.slug;
  const { data: related } = useQuery({
    queryKey: ["related", catSlug, slug],
    queryFn: () => getProducts({ category: catSlug, perPage: 10 }),
    enabled: Boolean(catSlug),
  });
  const relatedItems = (related?.items || []).filter((p) => p.slug !== slug);

  const off = useMemo(() => (product ? discountPercent(product.regularPrice, product.salePrice) : 0), [product]);
  const attrs = product?.attributes || [];
  const allSelected = attrs.every((a) => variant[a.name]);

  if (isLoading) {
    return (
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-8 lg:grid-cols-2">
        <div className="aspect-square rounded-2xl shimmer" />
        <div className="space-y-4">
          <div className="h-8 w-3/4 rounded shimmer" />
          <div className="h-5 w-1/3 rounded shimmer" />
          <div className="h-10 w-1/2 rounded shimmer" />
          <div className="h-12 w-full rounded-full shimmer" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <EmptyState title={t("no_products_title")} body={t("no_products_body")} action={<Link to="/shop" className="rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white">{t("continue_shopping")}</Link>} />
      </div>
    );
  }

  const wished = inWishlist(product.id);
  const inStock = product.inStock !== false;
  const canAdd = inStock && allSelected;

  const handleAdd = () => canAdd && addToCart(product, qty, Object.keys(variant).length ? variant : null);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      <nav className="mb-4 flex items-center gap-1 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-brand">{t("home")}</Link>
        <ChevronRight size={13} />
        {product.categories?.[0] && (
          <>
            <Link to={`/category/${product.categories[0].slug}`} className="hover:text-brand">{product.categories[0].name}</Link>
            <ChevronRight size={13} />
          </>
        )}
        <span className="line-clamp-1 text-ink">{product.name}</span>
      </nav>

      <div className="grid gap-8 lg:grid-cols-2">
        <Gallery images={product.images} name={product.name} />

        <div>
          <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl" data-testid="product-title">{product.name}</h1>
          <div className="mt-3 flex items-center gap-4">
            {product.rating > 0 ? <Rating rating={product.rating} count={product.reviewCount} size={16} /> : <span className="text-sm text-muted-foreground">{t("no_reviews")}</span>}
            <span className={`flex items-center gap-1 text-sm font-semibold ${inStock ? "text-success" : "text-destructive"}`}>{inStock ? t("in_stock") : t("out_of_stock")}</span>
          </div>

          <div className="mt-5 flex items-end gap-3">
            <span className="font-display text-3xl font-extrabold text-ink" data-testid="product-price">{formatPrice(product.price, product.currency)}</span>
            {off > 0 && (
              <>
                <span className="text-lg text-muted-foreground line-through">{formatPrice(product.regularPrice, product.currency)}</span>
                <span className="rounded-full bg-brand/10 px-2 py-0.5 text-sm font-bold text-brand">-{off}%</span>
              </>
            )}
          </div>

          {attrs.map((a) => (
            <div key={a.name} className="mt-6">
              <div className="mb-2 text-sm font-semibold text-ink">{a.name === "Color" ? t("color") : a.name === "Size" ? t("size") : a.name}</div>
              <div className="flex flex-wrap gap-2">
                {a.terms.map((term) => (
                  <button
                    key={term}
                    onClick={() => setVariant((v) => ({ ...v, [a.name]: term }))}
                    className={`min-w-11 rounded-full border px-4 py-2 text-sm font-medium transition ${variant[a.name] === term ? "border-brand bg-brand/10 text-brand" : "border-border hover:border-ink"}`}
                    data-testid={`variant-${a.name}-${term}`}
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-6 flex items-center gap-4">
            <div className="text-sm font-semibold text-ink">{t("quantity")}</div>
            <div className="flex items-center rounded-full border border-border">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-10 w-10 place-items-center" data-testid="qty-minus"><Minus size={16} /></button>
              <span className="w-10 text-center text-sm font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="grid h-10 w-10 place-items-center" data-testid="qty-plus"><Plus size={16} /></button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={handleAdd} disabled={!canAdd} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full border-2 border-ink text-sm font-bold text-ink transition hover:bg-ink hover:text-white disabled:opacity-40" data-testid="add-to-cart-btn">
              <ShoppingCart size={18} /> {allSelected ? t("add_to_cart") : t("select_options")}
            </button>
            <button onClick={() => { handleAdd(); }} disabled={!canAdd} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-brand text-sm font-bold text-brand-foreground transition hover:brightness-105 disabled:opacity-40" data-testid="buy-now-btn">
              {t("buy_now")}
            </button>
            <button onClick={() => toggleWishlist(product)} className="grid h-12 w-12 place-items-center rounded-full border-2 border-border hover:border-brand hover:text-brand" data-testid="wishlist-btn">
              <Heart size={20} className={wished ? "fill-brand text-brand" : ""} />
            </button>
          </div>

          <div className="mt-6 space-y-3 rounded-2xl border border-border p-4 text-sm">
            <div className="flex items-center gap-3"><MapPin size={18} className="text-brand" /><span className="text-muted-foreground">{t("ships_to")}:</span> <span className="font-medium">Worldwide</span></div>
            <div className="flex items-center gap-3"><Truck size={18} className="text-brand" /><span className="text-muted-foreground">{t("estimated_delivery")}:</span> <span className="font-medium">7–15 days</span></div>
            <div className="flex items-center gap-3"><ShieldCheck size={18} className="text-brand" /><span className="font-medium">{t("buyer_protection")}</span></div>
            <div className="flex items-center gap-3"><RotateCcw size={18} className="text-brand" /><span className="font-medium">{t("trust_returns")}</span></div>
          </div>
        </div>
      </div>

      <div className="mt-12">
        <div className="flex gap-6 border-b border-border">
          {["description", "specifications", "reviews"].map((tb) => (
            <button key={tb} onClick={() => setTab(tb)} className={`-mb-px border-b-2 pb-3 text-sm font-semibold transition ${tab === tb ? "border-brand text-ink" : "border-transparent text-muted-foreground"}`} data-testid={`tab-${tb}`}>
              {tb === "description" ? t("description") : tb === "specifications" ? t("specifications") : t("reviews_tab")}
            </button>
          ))}
        </div>
        <div className="py-6">
          {tab === "description" && (
            <div className="prose max-w-none text-sm leading-relaxed text-foreground/80" dangerouslySetInnerHTML={{ __html: product.description || product.shortDescription || t("no_products_body") }} />
          )}
          {tab === "specifications" && (
            attrs.length ? (
              <table className="w-full max-w-lg text-sm">
                <tbody>
                  {attrs.map((a) => (
                    <tr key={a.name} className="border-b border-border">
                      <td className="py-2.5 pe-6 font-medium text-ink">{a.name}</td>
                      <td className="py-2.5 text-muted-foreground">{a.terms.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-sm text-muted-foreground">{t("no_products_body")}</p>
          )}
          {tab === "reviews" && (
            <EmptyState icon={Star} title={t("no_reviews")} body={t("connect_store")} />
          )}
        </div>
      </div>

      {relatedItems.length > 0 && <ProductRail title={t("related_products")} products={relatedItems} accent />}
    </div>
  );
}
